/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Ganturin Vladimir <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {FormlyFieldConfig} from '@ngx-formly/core';
import {CompoApiService, Feature, FeatureFilter, NgxEasyTableEventObject, StoreService} from 'compob2b-libs';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ProductFilterService {
  features: Feature[];
  fieldsFeatures: FormlyFieldConfig[] = [];
  fieldsFeatures$: BehaviorSubject<FormlyFieldConfig[]>;
  ctnActiveFeatures$: BehaviorSubject<number>;
  gridEvent = new Subject<NgxEasyTableEventObject>();

  constructor(
    readonly compoApiService: CompoApiService,
    readonly storeService: StoreService) {

    this.fieldsFeatures$ = new BehaviorSubject<FormlyFieldConfig[]>([]);
    this.ctnActiveFeatures$ = new BehaviorSubject<number>(0);
  }

  event(event: NgxEasyTableEventObject): void {
    this.gridEvent.next(event);
  }

  events(): Observable<NgxEasyTableEventObject> {
    return this.gridEvent.asObservable();
  }

  nextCtnActiveFeatures(cnt: number): void {
    this.ctnActiveFeatures$.next(cnt);
  }

  getCtnActiveFeatures(): Observable<number> {
    return this.ctnActiveFeatures$.asObservable();
  }

  getFields(): Observable<FormlyFieldConfig[]> {
    return this.fieldsFeatures$.asObservable();
  }

  setFields(fields) {
    this.fieldsFeatures$.next(fields);
    if (fields.length === 0) {
      console.warn(fields);
      this.nextCtnActiveFeatures(0);
    }
  }

  filterProducts(uri, id, params: HttpParams): Observable<FormlyFieldConfig[]> {
    return this.compoApiService.getWithParams<FeatureFilter>(uri + '/get-filter-fields/' + id, params).pipe(
      map(response => this.configFields(response, params)
      )
    );
  }

  configFields(response, params): FormlyFieldConfig[] {

    const selectFields = [
      'classId'
    ];

    this.fieldsFeatures = [];
    if (response.data.featureProduct.length) {
      response.data.featureProduct.forEach(element => {
        if (element.type === 'checkbox') {
          let defaultValue = false;
          if (element.code === 'hasStock' && params.get('hasStock') !== null && params.get('hasStock') !== '0') {
            defaultValue = JSON.parse(params.get('hasStock'));
          }
          if (element.code === 'recommended' && params.get('recommended') !== null && params.get('recommended') !== '0') {
            defaultValue = JSON.parse(params.get('recommended'));
          }
          if (element.code === 'actionProduct' && params.get('actionProduct') !== null && params.get('actionProduct') !== '0') {
            defaultValue = JSON.parse(params.get('actionProduct'));
          }
          if (element.code === 'regularAssortment' && params.get('regularAssortment') !== null && params.get('regularAssortment') !== '0') {
            defaultValue = JSON.parse(params.get('regularAssortment'));
          }
          const fieldConfig: FormlyFieldConfig = {
            key: element.code,
            type: 'checkbox-custom',
            defaultValue: defaultValue,
            templateOptions: {
              label: element.header,
              placeholder: element.header
            }
          };
          this.fieldsFeatures.push(fieldConfig);
        }

        if (element.type === 'multiselect') {
          const optionsList = [];
          if (element.featureValue !== null) {
            element.featureValue.forEach(item => {
              optionsList.push({value: item.id, label: item.value});
            });
          }
          const defaultValue = [];
          if (params.get(element.code) !== null) {
            const values = params.get(element.code).split(',');

            optionsList.forEach(item => {
                if (values.includes(item.value.toString())) {
                  defaultValue.push(item);
                }
              }
            );
          }

          const fieldConfig: FormlyFieldConfig = {
            key: element.code,
            type: 'select-multi-filter-property',
            defaultValue,
            templateOptions: {
              label: element.header,
              placeholder: element.header,
              options: optionsList
            }
          };
          this.fieldsFeatures.push(fieldConfig);
        }

        if (element.type === 'select') {
          let optionsList = [];
          if (element.featureValue !== null) {
            optionsList = element.featureValue.map(value => {
              return {value: value.id, label: value.value};
            });
          }
          let defaultValue = 0;

          selectFields
            .filter(field => element.code === field && params.get(field) !== null)
            .forEach(field => defaultValue = optionsList[Number(params.get(field))]);

          const fieldConfig: FormlyFieldConfig = {
            key: element.code,
            type: 'select-filter-property',
            defaultValue,
            templateOptions: {
              label: element.header,
              placeholder: element.header,
              options: optionsList
            }
          };
          this.fieldsFeatures.push(fieldConfig);
        }

        if (element.type === 'select-custom') {
          let optionsList = [];
          if (element.featureValue !== null) {
            optionsList = element.featureValue.map(value => {
              return {value: value.id, label: value.value};
            });
          }
          let defaultValue = null;

          selectFields
            .filter(field => element.code === field && params.get(field) !== null)
            .forEach(field => {
              if (params.get(field) != undefined) {
                defaultValue = optionsList.filter(obj => {
                  return obj.value === Number(params.get(field));
                });
              }
            });

          const fieldConfig: FormlyFieldConfig = {
            key: element.code,
            type: 'select-custom',
            defaultValue: (defaultValue != null && defaultValue[0] !== undefined) ? defaultValue[0] : null,
            templateOptions: {
              label: element.header,
              placeholder: element.header,
              options: optionsList
            }
          };
          this.fieldsFeatures.push(fieldConfig);
        }

      });
    }

    /**
     * features
     */

    if (response.data.features !== null && response.data.features.length) {
      response.data.features.forEach(item => {
        const optionsList = item.values.map(value => {
          return {value: value.id, label: value.value, count: response.data.featureValueCount[value.id]};
        });
        // let defaultValue = [];
        // if (params.get(item.id.toString()) !== null) {
        //   const values = params.get(item.id.toString()).split(',');
        //   values.map(w => {
        //     w = parseInt(w, 0);
        //     return w;
        //   });
        //
        //   defaultValue = values;
        // }
        /**
         * checkbox
         */

        if (item.featureTypeId === 5) {
          let defaultValue1 = false;
          if (params.get(item.id.toString()) !== null && params.get(item.id.toString()) !== '0') {
            defaultValue1 = JSON.parse(params.get(item.id.toString()));
          }

          const fieldConfig: FormlyFieldConfig = {
            key: item.id,
            type: 'checkbox-custom',
            defaultValue: defaultValue1,
            templateOptions: {
              label: item.header,
              placeholder: item.header,
              options: optionsList
            }
          };
          this.fieldsFeatures.push(fieldConfig);
        }

        /**
         * interval
         */
        if (item.featureTypeId === 7 || item.featureTypeId === 4) {

          let defaultValueStart = '';
          let defaultValueEnd = '';
          const values1 = params.get(item.id.toString());
          if (values1 !== null && values1 !== undefined) {
            const valuesList = values1.split(';');
            defaultValueStart = valuesList[0];
            defaultValueEnd = valuesList[1];

          }
          if (response.data.minMax[item.id.toString()] !== undefined) {
            const fieldConfig: FormlyFieldConfig = {
              key: item.id,
              wrappers: ['interval-wrapper'],
              templateOptions: {label: item.header},
              fieldGroup: [{
                key: 'start',
                type: 'input',
                modelOptions: {
                  updateOn: 'blur',
                },
                defaultValue: defaultValueStart,
                templateOptions: {
                  type: 'number',
                  min: response.data.minMax[item.id].min,
                  max: response.data.minMax[item.id].max,
                  placeholder: 'от ' + response.data.minMax[item.id].min
                }
              },
                {
                  key: 'stop',
                  type: 'input',
                  modelOptions: {
                    updateOn: 'blur',
                  },
                  defaultValue: defaultValueEnd,
                  templateOptions: {
                    type: 'number',
                    min: response.data.minMax[item.id].min,
                    max: response.data.minMax[item.id].max,
                    placeholder: 'до ' + response.data.minMax[item.id].max
                  }
                }],
            };
            this.fieldsFeatures.push(fieldConfig);
          }
        }

        /**
         * select (multi)
         */

        if (item.featureTypeId === 1) {
          let optionsList = [];
          if (item.values !== null) {
            item.values.forEach(item => {
              optionsList.push({value: item.id, label: item.value});
            });
          }
          let defaultValue = [];
          if (params.get(item.id.toString()) !== null) {
            const values = params.get(item.id.toString()).split(',');

            optionsList.forEach(item => {
                if (values.includes(item.value.toString())) {
                  defaultValue.push(item);
                }
              }
            );
          }

          const fieldConfig: FormlyFieldConfig = {
            key: item.id,
            type: 'select-multi-filter-property',
            defaultValue,
            templateOptions: {
              label: item.header,
              placeholder: item.header,
              options: optionsList
            }
          };
          this.fieldsFeatures.push(fieldConfig);
        }

      });
    }

    return this.fieldsFeatures;
  }

  removeSubFeatures() {
    return this.fieldsFeatures$.pipe(
      map(item => item.filter(o => {
        return (/^-?[\d.]+(?:e-?\d+)?$/.test(o.key.toString()) === false && o.key.toString() !== 'classId');
      })));
  }
}
